import { Match, Participant, PostWithDetails, Tournament } from '@/src/domain/models';
import type { LeaderboardEntry } from '@/src/domain/usecases/GetBracketLeaderboardUseCase';
import type { RoundRobinStanding } from '@/src/domain/usecases/GetRoundRobinStandingsUseCase';
import { useTranslation } from '@/src/i18n';
import { FontAwesome5, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  Button,
  FlatList,
  Modal,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { createStyles } from './BracketScreen.styles';
import { EmptyState, PatternBackground, PostCard } from '@/src/presentation/components';

export interface MatchWithParticipants extends Match {
  participant1?: Participant;
  participant2?: Participant;
  winner?: Participant;
}

type TabKey = 'bracket' | 'leaderboard' | 'posts';

export interface BracketScreenProps {
  tournament: Tournament;
  matches: MatchWithParticipants[];
  participants: Participant[];
  champion: Participant | null;
  standings: RoundRobinStanding[];
  leaderboard: LeaderboardEntry[];
  posts: PostWithDetails[];
  postsLoading: boolean;
  currentUserId?: string;
  onUserPress: (userId: string) => void;
  onDeletePost: (postId: string) => void;
  onLikePress: (postId: string, isLiked: boolean) => void;
  onSharePost?: (postId: string) => void;
  onLoadMorePosts: () => void;
  canUpdateResults: boolean;
  userRole?: string;
  activeTab: TabKey;
  setActiveTab: (tab: TabKey) => void;
  modalVisible: boolean;
  setModalVisible: (visible: boolean) => void;
  selectedMatch: MatchWithParticipants | null;
  score1: string;
  setScore1: (score: string) => void;
  score2: string;
  setScore2: (score: string) => void;
  submitting: boolean;
  handleMatchPress: (match: MatchWithParticipants) => void;
  handleSubmitResult: (winnerId: string | null) => void;
  handleExportBracket: () => void;
  handleExportPDF: () => void;
  isExporting: boolean;
  isExportingPDF: boolean;
  viewRef: any;
  isPro?: boolean;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
    success: string;
    overlay: string;
    divider: string;
    badgeCompleted: string;
  };
}

export const BracketScreen = ({
  tournament,
  matches,
  champion,
  standings,
  leaderboard,
  posts,
  postsLoading,
  currentUserId,
  onUserPress,
  onDeletePost,
  onLikePress,
  onSharePost,
  onLoadMorePosts,
  canUpdateResults,
  userRole,
  activeTab,
  setActiveTab,
  modalVisible,
  setModalVisible,
  selectedMatch,
  score1,
  setScore1,
  score2,
  setScore2,
  submitting,
  handleMatchPress,
  handleSubmitResult,
  handleExportBracket,
  handleExportPDF,
  isExporting,
  isExportingPDF,
  viewRef,
  isPro,
  colors,
}: BracketScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const isRoundRobin = tournament.tournamentType === 'round_robin';
  const isSwiss = tournament.tournamentType === 'swiss';
  const isKingOfTheHill = tournament.tournamentType === 'king_of_the_hill';
  const usesStandings = isRoundRobin || isSwiss;
  const usesMatchList = usesStandings || isKingOfTheHill;

  const renderRoundRobinMatch = (match: MatchWithParticipants) => {
    const isCompleted = match.status === 'completed';
    const matchScore1 = match.scoreParticipant1 ?? 0;
    const matchScore2 = match.scoreParticipant2 ?? 0;
    const isDraw = isCompleted && matchScore1 === matchScore2;

    return (
      <TouchableOpacity
        key={match.id}
        style={[
          styles.matchCard,
          { backgroundColor: colors.card, borderColor: colors.border },
          isCompleted && { opacity: 0.8 },
          !canUpdateResults && { opacity: 0.7 },
        ]}
        onPress={() => handleMatchPress(match)}
        disabled={isCompleted || !canUpdateResults}
      >
        <View style={styles.roundRobinMatchRow}>
          <View style={styles.roundRobinTeam}>
            <Text
              style={[
                styles.roundRobinTeamName,
                { color: colors.text },
                isCompleted && matchScore1 > matchScore2 && { fontWeight: 'bold', color: colors.success },
              ]}
            >
              {match.participant1?.name}
            </Text>
          </View>
          {isCompleted ? (
            <View style={styles.roundRobinScore}>
              <Text style={[styles.roundRobinScoreText, { color: colors.primary }]}>{matchScore1}</Text>
              <Text style={[styles.roundRobinScoreDivider, { color: colors.textTertiary }]}>-</Text>
              <Text style={[styles.roundRobinScoreText, { color: colors.primary }]}>{matchScore2}</Text>
            </View>
          ) : (
            <Text style={[styles.roundRobinVs, { color: colors.textTertiary }]}>vs</Text>
          )}
          <View style={styles.roundRobinTeam}>
            <Text
              style={[
                styles.roundRobinTeamName,
                { color: colors.text },
                isCompleted && matchScore2 > matchScore1 && { fontWeight: 'bold', color: colors.success },
              ]}
            >
              {match.participant2?.name}
            </Text>
          </View>
        </View>
        {isDraw && <Text style={[styles.drawBadge, { color: colors.textTertiary }]}>{t('bracket.draw')}</Text>}
      </TouchableOpacity>
    );
  };

  const renderRoundRobinStandings = () => {
    if (!usesStandings) return null;

    return (
      <View style={[styles.standingsSection, { backgroundColor: colors.surface, borderColor: colors.border }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 15 }}>
          <Ionicons name="stats-chart" size={20} color={colors.primary} style={{ marginRight: 8 }} />
          <Text style={[styles.standingsTitle, { color: colors.primary }]}>{t('bracket.standings')}</Text>
        </View>

        <View style={styles.standingsTable}>
          <View style={[styles.standingsHeaderRow, { borderBottomColor: colors.border }]}>
            <Text style={[styles.standingsHeaderPos, { color: colors.textSecondary }]}>#</Text>
            <Text style={[styles.standingsHeaderName, { color: colors.textSecondary }]}>{t('bracket.team')}</Text>
            <Text style={[styles.standingsHeaderStat, { color: colors.textSecondary }]}>{t('bracket.played')}</Text>
            <Text style={[styles.standingsHeaderStat, { color: colors.textSecondary }]}>{t('bracket.won')}</Text>
            <Text style={[styles.standingsHeaderStat, { color: colors.textSecondary }]}>{t('bracket.drawn')}</Text>
            <Text style={[styles.standingsHeaderStat, { color: colors.textSecondary }]}>{t('bracket.lost')}</Text>
            {!isSwiss && <Text style={[styles.standingsHeaderStat, { color: colors.textSecondary }]}>{t('bracket.goalDifference')}</Text>}
            <Text style={[styles.standingsHeaderPts, { color: colors.textSecondary }]}>{t('bracket.points')}</Text>
          </View>

          {standings.map((standing, index) => (
            <View
              key={standing.participant.id}
              style={[
                styles.standingsRow,
                { borderBottomColor: colors.divider },
                index === 0 && tournament.status === 'completed' && { backgroundColor: colors.badgeCompleted },
              ]}
            >
              <Text style={[styles.standingsPos, { color: colors.text }]}>{index + 1}</Text>
              <Text style={[styles.standingsName, { color: colors.text }]} numberOfLines={1}>
                {standing.participant.name}
              </Text>
              <Text style={[styles.standingsStat, { color: colors.text }]}>{standing.played}</Text>
              <Text style={[styles.standingsStat, { color: colors.success }]}>{standing.won}</Text>
              <Text style={[styles.standingsStat, { color: colors.textTertiary }]}>{standing.drawn}</Text>
              <Text style={[styles.standingsStat, { color: colors.error }]}>{standing.lost}</Text>
              {!isSwiss && (
                <Text
                  style={[
                    styles.standingsStat,
                    { color: standing.goalDifference >= 0 ? colors.success : colors.error },
                  ]}
                >
                  {standing.goalDifference >= 0 ? '+' : ''}
                  {standing.goalDifference}
                </Text>
              )}
              <Text style={[styles.standingsPts, { color: colors.text }]}>{standing.points}</Text>
            </View>
          ))}
        </View>

        <View style={styles.standingsLegend}>
          <Text style={[styles.standingsLegendText, { color: colors.textTertiary }]}>
            {isSwiss ? t('bracket.legendSwiss') : t('bracket.legendRoundRobin')}
          </Text>
        </View>
      </View>
    );
  };

  const renderRoundRobinMatches = () => {
    if (!usesMatchList) return null;

    if (isKingOfTheHill) {
      // For KotH: show all matches in round order to reveal the gauntlet sequence
      const sortedMatches = [...matches].sort((a, b) => a.round - b.round);
      const completedMatches = sortedMatches.filter((m) => m.status === 'completed');
      const pendingMatches = sortedMatches.filter((m) => m.status === 'pending');
      const currentMatch = pendingMatches[0];

      return (
        <View style={styles.roundRobinMatchesSection}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 15 }}>
            <MaterialCommunityIcons name="crown" size={20} color={colors.primary} style={{ marginRight: 8 }} />
            <Text style={[styles.bracketTitle, { color: colors.primary }]}>{t('bracket.allMatches')}</Text>
          </View>

          {currentMatch && (
            <>
              <Text style={[styles.matchGroupTitle, { color: colors.text }]}>{t('bracket.pending')} (1)</Text>
              {renderRoundRobinMatch(currentMatch)}
            </>
          )}

          {pendingMatches.length > 1 && (
            <>
              <Text style={[styles.matchGroupTitle, { color: colors.text }]}>{t('bracket.upcoming')} ({pendingMatches.length - 1})</Text>
              {pendingMatches.slice(1).map(renderRoundRobinMatch)}
            </>
          )}

          {completedMatches.length > 0 && (
            <>
              <Text style={[styles.matchGroupTitle, { color: colors.text }]}>{t('bracket.completed')} ({completedMatches.length})</Text>
              {completedMatches.map(renderRoundRobinMatch)}
            </>
          )}
        </View>
      );
    }

    const completedMatches = matches.filter((m) => m.status === 'completed');
    const pendingMatches = matches.filter((m) => m.status === 'pending');

    return (
      <View style={styles.roundRobinMatchesSection}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 15 }}>
          <MaterialCommunityIcons name="soccer" size={20} color={colors.primary} style={{ marginRight: 8 }} />
          <Text style={[styles.bracketTitle, { color: colors.primary }]}>
            {isSwiss ? t('bracket.allRounds') : t('bracket.allMatches')}
          </Text>
        </View>

        {pendingMatches.length > 0 && (
          <>
            <Text style={[styles.matchGroupTitle, { color: colors.text }]}>{t('bracket.pending')} ({pendingMatches.length})</Text>
            {pendingMatches.map(renderRoundRobinMatch)}
          </>
        )}

        {completedMatches.length > 0 && (
          <>
            <Text style={[styles.matchGroupTitle, { color: colors.text }]}>{t('bracket.completed')} ({completedMatches.length})</Text>
            {completedMatches.map(renderRoundRobinMatch)}
          </>
        )}
      </View>
    );
  };

  const renderMatch = (match: MatchWithParticipants) => {
    const isCompleted = match.status === 'completed';
    const hasParticipants = match.participant1 && match.participant2;
    const isBracketReset = match.bracketType === 'grand_final' && match.round === 2;

    return (
      <TouchableOpacity
        key={match.id}
        style={[
          styles.matchCard,
          { backgroundColor: colors.card, borderColor: colors.border },
          isCompleted && { borderColor: colors.success },
          !hasParticipants && { opacity: 0.6 },
          isBracketReset && { borderColor: '#FF9500', borderWidth: 3 },
        ]}
        onPress={() => handleMatchPress(match)}
        disabled={!hasParticipants}
      >
        <View style={styles.matchHeader}>
          <Text style={[styles.matchTitle, { color: colors.text }]}>
            {match.bracketType === 'winners' && `${t('bracket.winnersRound')} ${match.round}`}
            {match.bracketType === 'losers' && `${t('bracket.losersRound')} ${match.round}`}
            {match.bracketType === 'second_losers' && `${t('bracket.secondLosersBracket')} - ${t('bracket.round')} ${match.round}`}
            {match.bracketType === 'grand_final' && match.round === 1 && t('bracket.grandFinal')}
            {match.bracketType === 'grand_final' && match.round === 2 && `🔄 ${t('bracket.grandFinalReset')}`}
            {` - ${t('bracket.match')} ${match.matchNumber}`}
          </Text>
          <Text style={[styles.matchStatus, { color: isCompleted ? colors.success : colors.textTertiary }]}>
            {isCompleted ? t('bracket.completed').toLowerCase() : t('bracket.pending').toLowerCase()}
          </Text>
        </View>

        {isBracketReset && (
          <View style={styles.resetBanner}>
            <Text style={styles.resetBannerText}>{t('bracket.bracketResetNote')}</Text>
          </View>
        )}

        <View style={styles.participantRow}>
          <Text
            style={[
              styles.participantName,
              { color: colors.text },
              match.winnerId === match.participant1Id && { color: colors.success, fontWeight: 'bold' },
            ]}
          >
            {match.participant1?.name || t('tournaments.tbd')}
          </Text>
          {match.scoreParticipant1 !== undefined && (
            <Text style={[styles.score, { color: colors.primary }]}>{match.scoreParticipant1}</Text>
          )}
        </View>

        <View style={styles.participantRow}>
          <Text
            style={[
              styles.participantName,
              { color: colors.text },
              match.winnerId === match.participant2Id && { color: colors.success, fontWeight: 'bold' },
            ]}
          >
            {match.participant2?.name || t('tournaments.tbd')}
          </Text>
          {match.scoreParticipant2 !== undefined && (
            <Text style={[styles.score, { color: colors.primary }]}>{match.scoreParticipant2}</Text>
          )}
        </View>

        {isCompleted && match.winner && (
          <Text style={[styles.winnerLabel, { color: colors.success }]}>{t('tournaments.winner')}: {match.winner.name}</Text>
        )}
      </TouchableOpacity>
    );
  };

  const renderBracket = (bracketType: 'winners' | 'losers' | 'second_losers' | 'grand_final') => {
    const bracketMatches = matches.filter((m) => m.bracketType === bracketType);
    const rounds = [...new Set(bracketMatches.map((m) => m.round))].sort((a, b) => a - b);

    if (bracketMatches.length === 0) return null;

    return (
      <View style={styles.bracketSection}>
        <View style={styles.bracketTitleContainer}>
          {bracketType === 'winners' && (
            <>
              <FontAwesome5 name="trophy" size={20} color={colors.primary} />
              <Text style={[styles.bracketTitle, { color: colors.primary }]}>{t('bracket.winnersBracket')}</Text>
            </>
          )}
          {bracketType === 'losers' && (
            <>
              <Ionicons name="refresh" size={20} color={colors.primary} />
              <Text style={[styles.bracketTitle, { color: colors.primary }]}>{t('bracket.losersBracket')}</Text>
            </>
          )}
          {bracketType === 'second_losers' && (
            <>
              <Ionicons name="refresh-circle" size={20} color={colors.primary} />
              <Text style={[styles.bracketTitle, { color: colors.primary }]}>{t('bracket.secondLosersBracket')}</Text>
            </>
          )}
          {bracketType === 'grand_final' && (
            <>
              <Ionicons name="trophy" size={20} color="#FFD700" />
              <Text style={[styles.bracketTitle, { color: colors.primary }]}>{t('bracket.grandFinal')}</Text>
            </>
          )}
        </View>
        {rounds.map((round) => {
          const roundMatches = bracketMatches.filter((m) => m.round === round);
          return (
            <View key={`${bracketType}-${round}`} style={styles.roundSection}>
              <Text style={[styles.roundTitle, { color: colors.text }]}>
                {bracketType === 'grand_final' && round === 2 ? t('bracket.bracketReset') : `${t('bracket.round')} ${round}`}
              </Text>
              {roundMatches.map(renderMatch)}
            </View>
          );
        })}
      </View>
    );
  };

  return (
    <>
      <View ref={viewRef} collapsable={false} style={styles.container}>
        <PatternBackground>
        {/* Header */}
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.title, { color: colors.text }]}>{tournament.name}</Text>
              <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                {tournament.tournamentType === 'single_elimination' && t('bracket.singleEliminationBracket')}
                {tournament.tournamentType === 'double_elimination' && t('bracket.doubleEliminationBracket')}
                {tournament.tournamentType === 'round_robin' && t('bracket.roundRobinTournament')}
                {tournament.tournamentType === 'swiss' && t('bracket.swissSystemTournament')}
                {tournament.tournamentType === 'triple_elimination' && t('bracket.tripleEliminationBracket')}
                {tournament.tournamentType === 'king_of_the_hill' && t('bracket.kingOfTheHillTournament')}
              </Text>
            </View>
            <TouchableOpacity
              style={[
                styles.moreButton,
                {
                  backgroundColor: colors.surface,
                  borderColor: colors.border,
                  opacity: isExporting || isExportingPDF ? 0.6 : 1,
                },
              ]}
              onPress={() => {
                platformAlert(
                  t('bracket.exportOptions'),
                  t('bracket.exportOptionsDesc'),
                  [
                    {
                      text: t('bracket.exportPDF'),
                      onPress: handleExportPDF,
                    },
                    {
                      text: t('bracket.shareImage'),
                      onPress: handleExportBracket,
                    },
                    {
                      text: t('common.cancel'),
                      style: 'cancel',
                    },
                  ],
                  { cancelable: true }
                );
              }}
              disabled={isExporting || isExportingPDF}
            >
              {isExporting || isExportingPDF ? (
                <ActivityIndicator size="small" color={colors.text} />
              ) : (
                <Ionicons name="ellipsis-horizontal" size={24} color={colors.text} />
              )}
            </TouchableOpacity>
          </View>
          {tournament.status === 'completed' && champion && (
            <View style={styles.championBanner}>
              <FontAwesome5 name="trophy" size={12} color="#B8860B" style={{ marginRight: 6 }} />
              <Text style={styles.championLabel}>{t('tournaments.champion').toUpperCase()}  </Text>
              <Text style={[styles.championName, { color: colors.text }]}>{champion.name}</Text>
            </View>
          )}
          {userRole && userRole !== 'owner' && (
            <View
              style={[
                styles.roleBadge,
                { backgroundColor: userRole === 'admin' ? colors.primary : colors.textTertiary },
              ]}
            >
              <Ionicons
                name={userRole === 'admin' ? 'shield-checkmark' : 'eye'}
                size={14}
                color="#fff"
                style={{ marginRight: 6 }}
              />
              <Text style={styles.roleBadgeText}>
                {userRole === 'admin' ? t('bracket.adminCanUpdate') : t('bracket.viewerReadOnly')}
              </Text>
            </View>
          )}
        </View>

        {/* Tab Bar */}
        <View style={[styles.tabBar, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <TouchableOpacity
            style={[styles.tabButton, activeTab === 'bracket' && styles.tabButtonActive]}
            onPress={() => setActiveTab('bracket')}
          >
            <Text
              style={[
                styles.tabButtonText,
                { color: activeTab === 'bracket' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('bracket.bracket')}
            </Text>
            {activeTab === 'bracket' && <View style={[styles.tabIndicator, { backgroundColor: colors.primary }]} />}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tabButton, activeTab === 'leaderboard' && styles.tabButtonActive]}
            onPress={() => setActiveTab('leaderboard')}
          >
            <Text
              style={[
                styles.tabButtonText,
                { color: activeTab === 'leaderboard' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('bracket.leaderboard')}
            </Text>
            {activeTab === 'leaderboard' && (
              <View style={[styles.tabIndicator, { backgroundColor: colors.primary }]} />
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tabButton, activeTab === 'posts' && styles.tabButtonActive]}
            onPress={() => setActiveTab('posts')}
          >
            <Text
              style={[
                styles.tabButtonText,
                { color: activeTab === 'posts' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('bracket.posts')}
            </Text>
            {activeTab === 'posts' && (
              <View style={[styles.tabIndicator, { backgroundColor: colors.primary }]} />
            )}
          </TouchableOpacity>
        </View>

        {/* Content */}
        {activeTab === 'bracket' && (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
            {usesStandings && renderRoundRobinStandings()}
            {usesMatchList ? (
              renderRoundRobinMatches()
            ) : (
              <>
                {renderBracket('winners')}
                {renderBracket('losers')}
                {renderBracket('second_losers')}
                {renderBracket('grand_final')}
              </>
            )}
          </ScrollView>
        )}

        {activeTab === 'leaderboard' && (
          <FlatList
            data={leaderboard}
            keyExtractor={(item) => item.participant.id}
            renderItem={({ item }) => (
              <View style={[styles.leaderboardItem, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.leaderboardPosition}>
                  <Text
                    style={[
                      styles.positionText,
                      {
                        color:
                          item.position === 1
                            ? '#FFD700'
                            : item.position === 2
                              ? '#C0C0C0'
                              : item.position === 3
                                ? '#CD7F32'
                                : colors.text,
                      },
                    ]}
                  >
                    {item.position === 1 ? '🥇' : item.position === 2 ? '🥈' : item.position === 3 ? '🥉' : `#${item.position}`}
                  </Text>
                </View>

                <View style={styles.leaderboardInfo}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Text style={[styles.leaderboardName, { color: colors.text }]}>{item.participant.name}</Text>
                    {item.participant.userId && <Ionicons name="person-circle" size={14} color={colors.primary} />}
                  </View>
                  <View style={styles.leaderboardStats}>
                    {tournament?.tournamentType === 'round_robin' && item.points !== undefined && (
                      <Text style={[styles.statText, { color: colors.primary }]}>{item.points} {t('bracket.pts')}</Text>
                    )}
                    <Text style={[styles.statText, { color: colors.success }]}>{item.wins}{t('bracket.w')}</Text>
                    <Text style={[styles.statText, { color: colors.error }]}>{item.losses}{t('bracket.l')}</Text>
                    {item.draws > 0 && (
                      <Text style={[styles.statText, { color: colors.textSecondary }]}>{item.draws}{t('bracket.d')}</Text>
                    )}
                    <Text style={[styles.statText, { color: colors.textSecondary }]}>
                      {item.winRate.toFixed(0)}% {t('bracket.wr')}
                    </Text>
                  </View>
                </View>

                {item.status === 'champion' && (
                  <View style={styles.championIcon}>
                    <FontAwesome5 name="crown" size={20} color="#FFD700" />
                  </View>
                )}
              </View>
            )}
            ListHeaderComponent={
              <View style={[styles.leaderboardHeader, { backgroundColor: colors.surface }]}>
                <Text style={[styles.leaderboardTitle, { color: colors.text }]}>
                  {tournament?.tournamentType === 'round_robin' ? t('bracket.standings') : t('bracket.rankings')}
                </Text>
                <Text style={[styles.leaderboardSubtitle, { color: colors.textSecondary }]}>
                  {tournament?.status === 'completed' ? t('bracket.finalResults') : t('bracket.liveRankings')}
                </Text>
              </View>
            }
            contentContainerStyle={styles.leaderboardContent}
            style={styles.content}
          />
        )}

        {activeTab === 'posts' && (
          <FlatList
            data={posts}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <PostCard
                post={item}
                currentUserId={currentUserId}
                onUserPress={onUserPress}
                onDeletePress={onDeletePost}
                onLikePress={onLikePress}
                onSharePress={onSharePost}
                colors={colors}
              />
            )}
            contentContainerStyle={styles.leaderboardContent}
            style={styles.content}
            onEndReached={onLoadMorePosts}
            onEndReachedThreshold={0.5}
            ListEmptyComponent={
              postsLoading ? null : (
                <EmptyState
                  icon={<Ionicons name="newspaper-outline" size={52} color={colors.textTertiary} />}
                  title={t('bracket.noPostsForTournament')}
                  subtitle={t('bracket.beFirstToPost')}
                  colors={colors}
                />
              )
            }
          />
        )}
        {/* PNG Watermark — only rendered during export for free users */}
        {isExporting && !isPro && (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                fontSize: 28,
                fontWeight: 'bold',
                color: 'rgba(255,255,255,0.35)',
                transform: [{ rotate: '-35deg' }],
                letterSpacing: 4,
              }}
            >
              TOURNALY FREE
            </Text>
          </View>
        )}
        </PatternBackground>
      </View>

      {/* Modal */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <Text style={[styles.modalTitle, { color: colors.text }]}>{t('bracket.enterMatchResult')}</Text>

            {selectedMatch && (
              <>
                <View style={styles.modalParticipant}>
                  <Text style={[styles.modalParticipantName, { color: colors.text }]}>
                    {selectedMatch.participant1?.name}
                  </Text>
                  <TextInput
                    style={[
                      styles.scoreInput,
                      { backgroundColor: colors.surface, borderColor: colors.border, color: colors.text },
                    ]}
                    placeholder={t('bracket.score')}
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={score1}
                    onChangeText={setScore1}
                  />
                </View>

                <View style={styles.modalParticipant}>
                  <Text style={[styles.modalParticipantName, { color: colors.text }]}>
                    {selectedMatch.participant2?.name}
                  </Text>
                  <TextInput
                    style={[
                      styles.scoreInput,
                      { backgroundColor: colors.surface, borderColor: colors.border, color: colors.text },
                    ]}
                    placeholder={t('bracket.score')}
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={score2}
                    onChangeText={setScore2}
                  />
                </View>

                {usesStandings && !isKingOfTheHill ? (
                  <>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>
                      {t('bracket.scoreHint')}{'\n'}{t('bracket.equalScoresDraw')}
                    </Text>
                    <TouchableOpacity
                      style={[styles.winnerButton, { backgroundColor: colors.primary }]}
                      onPress={() => handleSubmitResult(null)}
                      disabled={submitting || !score1 || !score2}
                    >
                      <Text style={[styles.winnerButtonText, { color: colors.primaryText }]}>{t('bracket.submitResult')}</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <>
                    <Text style={[styles.modalLabel, { color: colors.text }]}>{t('bracket.selectWinner')}</Text>

                    <TouchableOpacity
                      style={[styles.winnerButton, { backgroundColor: colors.primary }]}
                      onPress={() => handleSubmitResult(selectedMatch.participant1Id!)}
                      disabled={submitting}
                    >
                      <Text style={[styles.winnerButtonText, { color: colors.primaryText }]}>
                        {selectedMatch.participant1?.name}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.winnerButton, { backgroundColor: colors.primary }]}
                      onPress={() => handleSubmitResult(selectedMatch.participant2Id!)}
                      disabled={submitting}
                    >
                      <Text style={[styles.winnerButtonText, { color: colors.primaryText }]}>
                        {selectedMatch.participant2?.name}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}

                <Button title={t('common.cancel')} onPress={() => setModalVisible(false)} color={colors.error} />
              </>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
};

export const BracketScreenLoading = ({ colors }: { colors: { background: string; primary: string } }) => {
  const styles = createStyles();
  return (
    <View style={[styles.centerContainer, { backgroundColor: colors.background }]}>
      <ActivityIndicator size="large" color={colors.primary} />
    </View>
  );
};

export const BracketScreenError = ({ colors, message }: { colors: { background: string; error: string }; message: string }) => {
  const styles = createStyles();
  return (
    <View style={[styles.centerContainer, { backgroundColor: colors.background }]}>
      <Text style={[styles.errorText, { color: colors.error }]}>{message}</Text>
    </View>
  );
};
